import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getStoredUserEmail, isLoggedIn, logout as apiLogout, setTokens, fetchMe, bindPhone, bindWechatCode } from '@/lib/api';

interface User {
  id: number;
  email: string;
  phone?: string | null;
  wechat_openid?: string | null;
  nickname?: string | null;
  role?: string;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  bindPhone: (phone: string) => Promise<User>;
  bindWechat: (code: string) => Promise<User>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    if (!isLoggedIn()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const data = await fetchMe();
      const email = data.email || getStoredUserEmail();
      setUser({ ...data, email });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Handle OAuth callbacks (e.g. WeChat login) that return tokens in the URL.
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const email = params.get('email') || '';
    const wechatCode = params.get('wechat_code');

    const stripParams = (keys: string[]) => {
      keys.forEach((k) => params.delete(k));
      const remaining = params.toString();
      const newUrl = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    };
    // If this page was opened as a popup for WeChat login, close it so the parent can proceed.
    const closeWechatPopup = () => {
      if (window.opener && window.name === 'wechatLogin') {
        setTimeout(() => window.close(), 300);
      }
    };

    if (wechatCode) {
      // 新版后端：扫码回调只带一次性交换码，需调用 exchange 换取令牌
      stripParams(['wechat_code']);
      setLoading(true);
      fetch('/api/auth/wechat/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: wechatCode }),
      })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.access_token && data.refresh_token) {
            setTokens({ accessToken: data.access_token, refreshToken: data.refresh_token, user: { id: 0, email: data.email || '' } });
          }
        })
        .catch(() => { /* 交换失败则保持未登录，由用户重试扫码 */ })
        .finally(() => {
          closeWechatPopup();
          fetchUser();
        });
      return;
    }

    if (accessToken && refreshToken) {
      setTokens({ accessToken, refreshToken, user: { id: 0, email } });
      stripParams(['access_token', 'refresh_token', 'email', 'needs_phone_bind', 'is_new_user']);
      closeWechatPopup();
    }
    fetchUser();
  }, []);

  const logout = async () => {
    await apiLogout();
    setUser(null);
    window.location.href = '/login';
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const handleBindPhone = async (phone: string) => {
    const updated = await bindPhone(phone);
    setUser(prev => (prev ? { ...prev, phone: updated.phone, wechat_openid: updated.wechat_openid } : null));
    return updated;
  };

  const handleBindWechat = async (code: string) => {
    const updated = await bindWechatCode(code);
    setUser(prev => (prev ? { ...prev, phone: updated.phone, wechat_openid: updated.wechat_openid } : null));
    return updated;
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, refreshUser, bindPhone: handleBindPhone, bindWechat: handleBindWechat }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
